/**
 * Keeping a saved screen in step with the panel that is showing it.
 *
 * ## Which copy is master
 *
 * **The screen row is.** The section's own `columns`/`filter`/`sort` are a
 * *materialised view* of it: selecting a tab writes the screen's definition into
 * the section, and every edit after that goes to the section first and reaches
 * the screen on a debounce.
 *
 * That arrangement is not the tidiest imaginable — the definition is in two
 * places while both exist — and it is the one that keeps every existing path
 * working. The column picker, the filter builder, the sort and the width drag
 * all patch the section today, they are debounced and undoable there, and the
 * panel renders from there. Rewiring five editors onto a server call would trade
 * a duplicate for five new failure modes, and the duplicate is temporary: the
 * section's copy goes when the contract half of this lands.
 *
 * The consequence worth stating: **a screen row a beat behind the section is not
 * a loss.** The section is saved through the workspace either way, so a dropped
 * sync costs the screen row one revision, and the next edit carries it.
 *
 * ## What is never synced
 *
 * A club's screen and the product's. There is nowhere for an edit to go — the
 * server answers 404 to a write aimed at one — and a panel whose edits vanished
 * on reload would be worse than one that refused them. Making the club's half
 * *read-only on screen* is the overlay's job, not this module's; this only
 * guarantees that nothing here writes.
 */

import { useEffect, useRef } from "react";
import type { ScreenerConfig } from "@qufolio/model";

import { isOwn, type Screen } from "./screens";

/**
 * The parts of a panel a screen is kept in step with.
 *
 * **The universe is not one of them, and that is an open question rather than an
 * omission.** A screen row does carry `group_codes` — a club's "Momentum" is a
 * column set *over* a universe — but the panel's group chips are a separate
 * control the user changes constantly, and selecting a tab does not currently
 * move them. Until that is decided, the universe a screen was created with is
 * left alone: syncing it would make every chip click rewrite the screen, and
 * applying it would move the chips under the user on every tab change.
 *
 * The save path therefore has to send the screen's *stored* group codes back,
 * because the route replaces rather than merges — omitting them would blank
 * them.
 */
export interface Definition {
  columns: NonNullable<ScreenerConfig["columns"]>;
  filter: ScreenerConfig["filter"];
  sort: NonNullable<ScreenerConfig["sort"]>;
  /**
   * The named condition library — what the conditions popup lists.
   *
   * **Part of the screen, which is a reversal.** It was one library per panel,
   * shared by every tab, on the argument that a library is a library and editing
   * one entry should reach everything referencing it. What that meant in
   * practice: deleting a condition while looking at one screen emptied the
   * popup on all of them, with nothing on screen to say the list was shared.
   * That reads as data loss whatever the design says, and a screen whose columns
   * and filter travel but whose conditions do not is a half-answer to "what is a
   * screen". `docs/plans/screener-dsl.md` records the reversal.
   */
  savedFilters: NonNullable<ScreenerConfig["savedFilters"]>;
  /**
   * The order the picker lists the library's categories in. Travels **with the
   * library**: a screen that brings its own conditions brings their order, and
   * one falling back to the panel's library keeps the panel's order. Empty is
   * alphabetical, the way every screen listed before the field existed.
   */
  savedFilterCategoryOrder: NonNullable<ScreenerConfig["savedFilterCategoryOrder"]>;
  /**
   * The bar's ▲▼ key, saved with the screen (owner, 2026-10-29). Present only
   * when set, so every screen saved before it compares equal on All.
   */
  direction?: ScreenerConfig["direction"];
}

/**
 * One panel's definition, normalised.
 *
 * Absent and empty are the same thing here, because the store collapses an empty
 * optional on the way out — so a screen saved with no sorts comes back with none
 * rather than with `[]`, and a comparison that told those apart would resend the
 * whole definition on every load.
 *
 * `savedFilters` collapses the same way **on the way up and not on the way
 * down**: what this sends is what the panel holds, so a screen that has never
 * carried a library gets the panel's on the first edit. The asymmetry is
 * `applyScreenTo`'s, and the reason is written there.
 */
export function definitionOf(config: ScreenerConfig): Definition {
  return {
    columns: config.columns ?? [],
    filter: config.filter,
    sort: config.sort ?? [],
    savedFilters: config.savedFilters ?? [],
    savedFilterCategoryOrder: config.savedFilterCategoryOrder ?? [],
    ...(config.direction ? { direction: config.direction } : {}),
  };
}

/**
 * Whether two definitions say the same thing.
 *
 * Structural, through JSON, and that is a deliberate choice rather than laziness:
 * the things being compared are contract documents — arrays whose order is
 * meaningful and objects whose keys the store already normalises — so a
 * field-by-field comparison would be a second copy of the contract's shape,
 * needing an edit every time a column gains a property. The one thing it must
 * not do is compare object identity, which changes on every patch.
 */
export function sameDefinition(a: Definition, b: Definition): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** A baseline that matches no definition. `JSON.stringify` never returns "". */
const UNKNOWN = "";

/** How long after the last edit the screen is written. The workspace's 600ms. */
export const SYNC_DELAY_MS = 600;

export interface ScreenSyncOptions {
  /** The panel, as it stands now. */
  config: ScreenerConfig;
  /** The catalog, so ownership can be checked without another request. */
  screens: readonly Screen[];
  /** Writes the definition. Injected so this is testable without a server. */
  save: (key: string, definition: Definition) => Promise<unknown>;
  delay?: number;
}

/**
 * Push the panel's definition to the screen it is showing, after it settles.
 *
 * **Nothing is written until the definition actually differs from what this
 * screen was last known to hold.** Selecting a tab writes its own definition
 * into the section, so without that check every select would echo straight back
 * to the server — and a width drag fires a patch per frame, of which only the
 * last is worth a request.
 *
 * The baseline is per key, so switching away and back does not resend a screen
 * nobody edited.
 */
export function useScreenSync({ config, screens, save, delay = SYNC_DELAY_MS }: ScreenSyncOptions) {
  const lastSaved = useRef(new Map<string, string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const key = config.screenRef;
  const screen = key ? screens.find((s) => s.key === key) : undefined;
  const mine = screen !== undefined && isOwn(screen);
  const definition = definitionOf(config);
  const encoded = JSON.stringify(definition);

  useEffect(() => {
    if (!key || !mine) return;
    // First sight of a screen is its baseline, not an edit of it: the panel was
    // just filled from this definition.
    const seen = lastSaved.current;
    if (!seen.has(key)) {
      seen.set(key, encoded);
      return;
    }
    if (seen.get(key) === encoded) return;

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      seen.set(key, encoded);
      // **A failure marks the baseline unknown rather than deleting it.**
      // Deleting makes the next edit look like *first sight* of the screen,
      // which is the one case that deliberately does not save — so a single
      // failed write would have silently stopped syncing that screen for the
      // rest of the session. `""` is a safe sentinel: `JSON.stringify` never
      // produces it, so it differs from every definition and the next edit
      // resends the whole thing. There is nothing partial to repair.
      void save(key, JSON.parse(encoded) as Definition).catch(() => {
        seen.set(key, UNKNOWN);
      });
    }, delay);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [key, mine, encoded, delay, save]);
}

/**
 * Fill a panel from a saved screen.
 *
 * **Here rather than in a component, because two of them need it and they are in
 * different subtrees.** The tab strip lives in the editor and calls this when a
 * tab is picked; the panel calls it on mount, which has to work with the editor
 * shut. A copy in each would be two answers to "what does selecting a screen
 * do", and the one that drifts is the one nobody is looking at.
 *
 * One patch, so it is one undo and one save — `PresetsTab`'s rule, and this is
 * the same act with the reference written alongside. Sorts, the filter and the
 * named conditions travel with the columns, because a column set without the
 * conditions that made it useful is not the screen anybody saved.
 *
 * **Nothing is dispatched when the panel already holds that definition**, which
 * is what stops an ordinary page load — where the section is already a copy of
 * the screen — from marking the workspace dirty and writing it straight back.
 *
 * A screen that cannot be read leaves the panel alone rather than blanking it:
 * 404 covers "deleted" and "no longer yours" alike, and the definition already
 * in the section is the last good one.
 *
 * ## Two rules about the condition library
 *
 * **A screen with no `savedFilters` at all keeps the panel's**, because that is
 * every screen written before conditions travelled with one. `null` there means
 * "I have nothing to say about the library", not "the library is empty" — so the
 * panel's own list stands and the next edit writes it up. Without that
 * distinction the first tab click after this shipped would have emptied the
 * popup for everybody, which is the failure this whole change is about. `[]`
 * *is* an answer and is honoured: somebody deleted the last one.
 *
 * **Ticks are pruned to the library that arrives, never carried over whole.**
 * `activeFilterRefs` is ids, and ids from the screen you just left name nothing
 * in the screen you arrived at. Carrying them was not cosmetic: the picker reads
 * them, and the next click in it runs `applySelection`, which rebuilds `filter`
 * from those refs — so a stale tick silently overwrote the incoming screen's
 * real conditions with a combination of another screen's. Pruning rather than
 * clearing keeps the ticks that still mean something, which is the ordinary case
 * while a screen is still falling back to the panel's library.
 */
export async function applyScreenTo(input: {
  key: string;
  config: ScreenerConfig;
  load: (key: string) => Promise<{
    columns?: ScreenerConfig["columns"];
    filter?: ScreenerConfig["filter"];
    sort?: ScreenerConfig["sort"];
    /** Absent or null means the screen predates the column — see above. */
    savedFilters?: ScreenerConfig["savedFilters"] | null;
    savedFilterCategoryOrder?: ScreenerConfig["savedFilterCategoryOrder"] | null;
    recs?: ScreenerConfig["recs"] | null;
    topDown?: ScreenerConfig["topDown"] | null;
    seasonality?: ScreenerConfig["seasonality"] | null;
    direction?: ScreenerConfig["direction"];
  } | null>;
  onConfigChange: (patch: {
    screenRef: string;
    columns: NonNullable<ScreenerConfig["columns"]>;
    filter: ScreenerConfig["filter"];
    sort: NonNullable<ScreenerConfig["sort"]>;
    savedFilters: NonNullable<ScreenerConfig["savedFilters"]>;
    savedFilterCategoryOrder: NonNullable<ScreenerConfig["savedFilterCategoryOrder"]>;
    activeFilterRef: ScreenerConfig["activeFilterRef"];
    activeFilterRefs: NonNullable<ScreenerConfig["activeFilterRefs"]>;
    /** Undefined clears it: the screen picked is an ordinary one. */
    recs: ScreenerConfig["recs"];
    /** Undefined clears it: the screen picked is not a TopDown. */
    topDown: ScreenerConfig["topDown"];
    /** Undefined clears it: the screen picked is not a Seasonality screen. */
    seasonality: ScreenerConfig["seasonality"];
    /** Undefined clears it: the screen picked was saved on All. */
    direction: ScreenerConfig["direction"];
  }) => void;
}): Promise<void> {
  const screen = await input.load(input.key);
  if (!screen) return;
  const incoming: Definition = {
    columns: screen.columns ?? [],
    filter: screen.filter,
    sort: screen.sort ?? [],
    // `??` and not `|| []`: an empty library is an answer, a missing one is not.
    savedFilters: screen.savedFilters ?? input.config.savedFilters ?? [],
    // With the library it belongs to: the screen's own when it brings one.
    savedFilterCategoryOrder:
      screen.savedFilters != null
        ? (screen.savedFilterCategoryOrder ?? [])
        : (input.config.savedFilterCategoryOrder ?? []),
    ...(screen.direction ? { direction: screen.direction } : {}),
  };

  const recs = recsFor(screen.recs ?? undefined, input.config.recs);
  const topDown = topDownFor(screen.topDown ?? undefined, input.config.topDown);
  const seasonality = seasonalityFor(screen.seasonality ?? undefined, input.config.seasonality);

  const library = new Set(incoming.savedFilters.map((f) => f.id));
  const activeFilterRefs = (input.config.activeFilterRefs ?? []).filter((id) =>
    library.has(id),
  );
  const activeFilterRef =
    input.config.activeFilterRef && library.has(input.config.activeFilterRef)
      ? input.config.activeFilterRef
      : undefined;

  if (
    input.config.screenRef === input.key &&
    sameDefinition(definitionOf(input.config), incoming) &&
    // The ticks too, or a load that pruned a dangling ref would leave it in
    // place — the definition is equal in exactly the case where the panel is
    // already a copy of the screen, which is every page load.
    sameRefs(input.config, activeFilterRef, activeFilterRefs) &&
    sameRecs(input.config.recs, recs) &&
    JSON.stringify(input.config.topDown ?? null) === JSON.stringify(topDown ?? null) &&
    JSON.stringify(input.config.seasonality ?? null) === JSON.stringify(seasonality ?? null)
  ) {
    return;
  }
  input.onConfigChange({
    screenRef: input.key,
    ...incoming,
    activeFilterRef,
    activeFilterRefs,
    recs,
    topDown,
    seasonality,
    // Named, not left to the spread: absent from `incoming` would keep the
    // panel's ▼ on a screen saved on All.
    direction: incoming.direction,
  });
}

/**
 * The Seasonality screen a screen brings, keeping the panel's own bar.
 *
 * Mode, basis, years, floors and the start date are the reader's, as Recs'
 * D / W / M are: a refill over the same base keeps what is on the panel, and a
 * different base (or none held) takes the screen's as published.
 */
export function seasonalityFor(
  incoming: ScreenerConfig["seasonality"],
  held: ScreenerConfig["seasonality"],
): ScreenerConfig["seasonality"] {
  if (!incoming) return undefined;
  if (held && held.base === incoming.base) return held;
  return incoming;
}

/**
 * The TopDown a screen brings, keeping the panel's ticks over the same base.
 *
 * Ticks are saved with the panel (owner, 2026-10-16) and a screen never
 * carries any, so a refill must not drop them — unless the base changed, when
 * the sectors and industries ticked may not exist under it.
 */
export function topDownFor(
  incoming: ScreenerConfig["topDown"],
  held: ScreenerConfig["topDown"],
): ScreenerConfig["topDown"] {
  if (!incoming) return undefined;
  const { ticks: _ignored, ...published } = incoming;
  if (!held?.ticks || held.base !== incoming.base) return published;
  return { ...published, ticks: held.ticks };
}

/**
 * The trade list a screen brings, keeping the panel's own bar and side.
 *
 * D / W / M and Long / Short are the reader's choice — a member flipping to
 * daily should not be put back on weekly every time the screen is refilled —
 * so they survive when the panel already shows a list of the **same kind**.
 * A different kind, or none, takes the screen's as published.
 */
export function recsFor(
  incoming: ScreenerConfig["recs"],
  held: ScreenerConfig["recs"],
): ScreenerConfig["recs"] {
  if (!incoming) return undefined;
  if (!held || held.kind !== incoming.kind) return incoming;
  return { ...incoming, frequency: held.frequency, ...(held.direction ? { direction: held.direction } : {}) };
}

function sameRecs(a: ScreenerConfig["recs"], b: ScreenerConfig["recs"]): boolean {
  return (a?.kind ?? "") === (b?.kind ?? "") && (a?.frequency ?? "") === (b?.frequency ?? "")
    && (a?.direction ?? "") === (b?.direction ?? "");
}

/** Whether the panel's ticks already say what the incoming screen's do. */
function sameRefs(
  config: ScreenerConfig,
  ref: ScreenerConfig["activeFilterRef"],
  refs: readonly string[],
): boolean {
  const held = config.activeFilterRefs ?? [];
  return (
    config.activeFilterRef === ref &&
    held.length === refs.length &&
    held.every((id, i) => id === refs[i])
  );
}
